<script lang="ts" setup>
import { Timestamp } from "firebase/firestore";
import { EvaluationResults } from "types/EvaluationResults";
import { EvaluationTest } from "types/EvaluationTest";

import { Subscription } from "rxjs";

const props = defineProps({
  evaluationTest: {
    type: Object as PropType<EvaluationTest>,
    required: true,
  },
});

const emit = defineEmits(["saveTest"]);

const isGeneratingMetricStructure = ref(false);
const generateMetricStructure = async () => {
  if (!props.evaluationTest) return;

  isGeneratingMetricStructure.value = true;

  const response = await $fetch(
    `/api/${props.evaluationTest.originalReportedProblem.documentId}/${props.evaluationTest.originalReportedProblem.revisionId}/${props.evaluationTest.originalReportedProblem.checklistCriteriaId}/metric`,
    {
      method: "GET",
      headers: await useApiHeaders(),
    }
  );

  props.evaluationTest.criteriaConfig = {
    cretedAtTimestamp: Timestamp.now(),
    criteria: response,
  };

  emit("saveTest");

  isGeneratingMetricStructure.value = false;
};

const isRunningTest = ref(false);
const runTest = async () => {
  if (!props.evaluationTest) return;

  isRunningTest.value = true;

  try {
    await $fetch(`/api/sys-admin/run/test/${props.evaluationTest.id}`, {
      method: "POST",
      headers: await useApiHeaders(),
      body: {
        path: "",
      },
    });
  } catch (error) {
    console.error(error);
    useBaseToast("Oops! Something went wrong while running the test.", "error");
  }
  isRunningTest.value = false;
};

const evaluationTestResults = ref<EvaluationResults[]>([]);

const isInitialized = ref(false);

const evaluationTestResultsStore = useEvaluationTestResults(
  props.evaluationTest
)();

const subscription = ref<Subscription | undefined>();

onMounted(async () => {
  subscription.value = evaluationTestResultsStore
    .streamEvaluationTestResults()
    .subscribe((results) => {
      evaluationTestResults.value = results;
    });

  await evaluationTestResultsStore.initialize();

  isInitialized.value = true;
});

onUnmounted(() => {
  subscription.value?.unsubscribe();
});

const removeExpectedResults = () => {
  if (!props.evaluationTest) return;

  props.evaluationTest.expectedResultsId = null;
  emit("saveTest");
};

const onIsReadyToTest = () => {
  if (!props.evaluationTest) return;

  props.evaluationTest.status = EvaluationTestStatus.readyForTesting;
  props.evaluationTest.isControl = true;
  emit("saveTest");
};

const onIsReadyForTeacherReview = () => {
  if (!props.evaluationTest) return;

  props.evaluationTest.status = EvaluationTestStatus.teacherReview;
  emit("saveTest");
};
</script>

<template>
  <div>
    <BaseLoading v-if="isInitialized != true" />
    <div class="grow flex flex-col min-h-0" v-if="isInitialized == true">
      <div class="flex flex-row items-center justify-between">
        <div class="flex flex-row items-center">
          <BaseTextButton
            class="text-body-1"
            :show-spinner="isGeneratingMetricStructure"
            @click="generateMetricStructure"
          >
            Generate Metric Structure
          </BaseTextButton>
          <icon
            v-if="evaluationTest?.criteriaConfig"
            class="text-success-default ml-2"
            name="material-symbols:check-circle-rounded"
          />
        </div>

        <div class="flex flex-row items-center">
          <BaseTextButton @click="runTest" :show-spinner="isRunningTest">
            Run Test
          </BaseTextButton>
          <BaseTextButton @click="onIsReadyForTeacherReview">
            Ready for Teacher Review
          </BaseTextButton>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-between border-t border-dashed pt-2 mt-2"
        v-if="evaluationTest?.expectedResultsId"
      >
        <BaseTextButton @click="removeExpectedResults">
          Clear Expected Results
        </BaseTextButton>
        <BaseTextButton @click="onIsReadyToTest">
          Ready for Testing
        </BaseTextButton>
      </div>
      <div class="flex flex-col grow min-h-0 overflow-auto">
        <pre></pre>
        <DocumentProblemReviewTestingResult
          v-for="evaluationResult in evaluationTestResults"
          :key="evaluationResult.id!"
          :evaluation-test="evaluationTest"
          :evaluation-results="evaluationResult"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
